import { css } from "@emotion/react";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { RiCloseLine } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { IconButton } from "@itandi/itandi-bb-ui/IconButton";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { Resizable } from "@itandi/itandi-bb-ui/Resizable";
import { ResizableGroup } from "@itandi/itandi-bb-ui/ResizableGroup";
import type { ReactNode } from "react";
import { useCallback, useRef, useSyncExternalStore } from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { ChatMessageArea } from "~/components/common/chat/messages/ChatMessageArea";
import { ChatPostArea } from "~/components/common/chat/post/ChatPostArea";
import { SERVER_ERROR_MESSAGE } from "~/constants/AppConst";
import { messageCacheKey } from "~/hooks/deals/useMessages";
import { dealCacheKey } from "~/hooks/useDeals";
import { messageCacheKey as vendorMessageCacheKey } from "~/hooks/vendors/deals/useMessages";
import { dealCacheKey as vendorDealCacheKey } from "~/hooks/vendors/useDeals";
import type { Deal } from "~/models/Deal";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import type { Message, MessageReceiver } from "~/models/Message";
import { UPDATE_TEMPORARY_UNREAD_MESSAGE } from "~/models/Message";
import { isSuccess } from "~/utils/api/api_handler";
import { postTemporaryUnread as postClientTemporaryUnread } from "~/utils/api/deals/Message";
import { postTemporaryUnread as postVendorTemporaryUnread } from "~/utils/api/vendors/deals/Message";
import Styles from "./WithChat.module.scss";

type Props = DeepReadonly<{
	children: ReactNode;
	deal: Deal;
	enabled: boolean;
	isAdmin: boolean;
	isOpen: boolean;
	isVendor?: boolean;
	messages: Message[];
	onClose: () => void;
	receiver: MessageReceiver;
}>;

export function WithChat({
	enabled,
	children,
	deal,
	isAdmin,
	isOpen,
	isVendor = false,
	messages,
	onClose,
	receiver
}: Props) {
	const { mutate: globalMutate } = useSWRConfig();
	const isManagementTypeOwn =
		getOriginDealVendor(deal)?.companyVendor?.dealManagementTypeOwn;

	const mutateDeal = useCallback(async (): Promise<void> => {
		await globalMutate(
			isVendor ? vendorDealCacheKey(deal.id) : dealCacheKey(deal.id)
		);
	}, [globalMutate, isVendor, deal.id]);

	const mutateMessages = useCallback(async (): Promise<void> => {
		await globalMutate(
			isVendor ? vendorMessageCacheKey(deal.id) : messageCacheKey(deal.id)
		);
	}, [globalMutate, isVendor, deal.id]);

	const handleMutate = useCallback(async (): Promise<void> => {
		await mutateDeal();
		await mutateMessages();
	}, [mutateDeal, mutateMessages]);

	const chatRef = useRef<HTMLDivElement>(null);
	const chatHeight = useSyncExternalStore(
		(subscribe) => {
			window.addEventListener("resize", subscribe);
			window.addEventListener("scroll", subscribe);
			return () => {
				window.removeEventListener("resize", subscribe);
				window.removeEventListener("scroll", subscribe);
			};
		},
		() =>
			Math.round(
				window.innerHeight - (chatRef.current?.getBoundingClientRect().top ?? 0)
			),
		() => 0
	);

	const isDisabledTemporaryUnread =
		isAdmin ||
		!!deal.messageSummaryUnread?.unread ||
		!!deal.messageSummaryUnread?.displayUnread;

	const onTemporaryUnread = useCallback(async (): Promise<void> => {
		const postTemporaryUnread = isVendor
			? postVendorTemporaryUnread
			: postClientTemporaryUnread;
		const result = await postTemporaryUnread(deal.id);
		if (isSuccess(result)) {
			toast.success(UPDATE_TEMPORARY_UNREAD_MESSAGE);
			onClose();
			await mutateDeal();
		} else {
			toast.error(SERVER_ERROR_MESSAGE);
		}
	}, [isVendor, deal.id, onClose, mutateDeal]);

	if (!enabled || isManagementTypeOwn) {
		return children;
	}

	return (
		<ResizableGroup
			direction="horizontal"
			initialSizes={["1fr", { max: 560, min: 300, size: 560 }]}
		>
			<Resizable>
				<div css={css({ containerType: "inline-size" })}>{children}</div>
			</Resizable>
			<Resizable>
				<div
					className={Styles.ChatArea}
					ref={chatRef}
					style={{ height: chatHeight }}
				>
					<div className={Styles.Header}>
						<Flex alignItems="center" justifyContent="space-between">
							<Label weight="bold">
								{isVendor ? "管理会社とチャット" : "施工会社とチャット"}
							</Label>
							<Flex alignItems="center" gap="sm">
								{messages.length > 0 && (
									<Button
										disabled={isDisabledTemporaryUnread}
										height="small"
										onClick={onTemporaryUnread}
										variant="secondary"
									>
										未読ラベルをつける
									</Button>
								)}
								<IconButton
									Icon={RiCloseLine}
									onClick={onClose}
									variant="contained"
								/>
							</Flex>
						</Flex>
					</div>
					<ChatMessageArea
						deal={deal}
						handleMutate={handleMutate}
						isOpen={isOpen}
						messages={messages}
						receiver={receiver}
					/>
					<ChatPostArea
						deal={deal}
						handleMutate={handleMutate}
						isVendor={isVendor}
					/>
				</div>
			</Resizable>
		</ResizableGroup>
	);
}
